import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { PERMISSIONS } from './config/permissions';
import './App.css';
// Lazy load all page components for faster initial load
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const OrdersPage = lazy(() => import('./pages/OrdersPage').then(m => ({ default: m.OrdersPage })));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })));
const GenerateInvoicePage = lazy(() => import('./pages/GenerateInvoicePage').then(m => ({ default: m.GenerateInvoicePage })));
const LedgerPage = lazy(() => import('./pages/LedgerPage').then(m => ({ default: m.LedgerPage })));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage').then(m => ({ default: m.ChangePasswordPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const SellersManagementPage = lazy(() => import('./pages/SellersManagementPage').then(m => ({ default: m.SellersManagementPage })));
const PickupListPage = lazy(() => import('./pages/PickupListPage'));
const ReturnListPage = lazy(() => import('./pages/ReturnListPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const StockReportPage = lazy(() => import('./pages/StockReportPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
const OrderDiscountsPage = lazy(() => import('./pages/OrderDiscountsPage'));
const RolesManagementPage = lazy(() => import('./pages/RolesManagementPage'));
const QRCodeGeneratorPage = lazy(() => import('./pages/QRCodeGeneratorPage'));
const CreateProductPage = lazy(() => import('./pages/CreateProductPage'));
const ProductApprovalPage = lazy(() => import('./pages/ProductApprovalPage'));
const DraftProductsPage = lazy(() => import('./pages/DraftProductsPage'));
const DraftReviewPage = lazy(() => import('./pages/DraftReviewPage'));
const EditProductPage = lazy(() => import('./pages/EditProductPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ProductTypesManagementPage = lazy(() => import('./pages/ProductTypesManagementPage'));
const ProductTagsManagementPage = lazy(() => import('./pages/ProductTagsManagementPage'));
const ProductOptionsManagementPage = lazy(() => import('./pages/ProductOptionsManagementPage'));
const CustomFieldsManagementPage = lazy(() => import('./pages/CustomFieldsManagementPage'));
const ShopifySyncConfig = lazy(() => import('./pages/ShopifySyncConfig'));
const ShopifySyncSettingsPage = lazy(() => import('./pages/ShopifySyncSettingsPage'));
const HistoricalMappingsPage = lazy(() => import('./pages/HistoricalMappingsPage'));
const ShippingDefaultsPage = lazy(() => import('./pages/ShippingDefaultsPage'));
const ReturnScanPage = lazy(() => import('./pages/ReturnScanPage'));
const CustomerBlacklistPage = lazy(() => import('./pages/CustomerBlacklistPage'));
const MarketingPushPage = lazy(() => import('./pages/MarketingPushPage'));
const PushAnalyticsPage = lazy(() => import('./pages/PushAnalyticsPage'));
const MarketPriceUpliftsPage = lazy(() => import('./pages/MarketPriceUpliftsPage'));
const MarketingCollectionsPage = lazy(() => import('./pages/MarketingCollectionsPage'));
const WhatsAppButtonPage = lazy(() => import('./pages/WhatsAppButtonPage'));
const SearchAnalyticsPage = lazy(() => import('./pages/SearchAnalyticsPage'));
const SearchMappingsPage = lazy(() => import('./pages/SearchMappingsPage'));
const SearchFiltersPage = lazy(() => import('./pages/SearchFiltersPage'));
const ImageVectorsPage = lazy(() => import('./pages/ImageVectorsPage'));
const CatalogRulesPage = lazy(() => import('./pages/CatalogRulesPage'));
const SizeCategoriesPage = lazy(() => import('./pages/SizeCategoriesPage'));
const ColorFamiliesPage = lazy(() => import('./pages/ColorFamiliesPage'));
const CompetitorAnalysisPage = lazy(() => import('./pages/CompetitorAnalysisPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage').then(m => ({ default: m.SetPasswordPage })));

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
  </div>
);

// Save current route to sessionStorage so PWA can resume after iOS kills the process
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/login') {
      sessionStorage.setItem('taminzo_last_route', location.pathname + location.search);
    }
  }, [location]);
  return null;
};

const DefaultRedirect = () => {
  // On PWA resume, go back to last visited page
  const lastRoute = sessionStorage.getItem('taminzo_last_route');
  return <Navigate to={lastRoute || '/dashboard'} replace />;
};

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <Router>
          <RouteTracker />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/setup-password" element={<SetPasswordPage />} />
            <Route path="/reset-password" element={<SetPasswordPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DefaultRedirect />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="orders" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
                  <OrdersPage />
                </ProtectedRoute>
              } />
              <Route path="products" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_VIEW}>
                  <ProductsPage />
                </ProtectedRoute>
              } />
              <Route path="products/stock-report" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_STOCK_REPORT}>
                  <StockReportPage />
                </ProtectedRoute>
              } />
              <Route path="products/marketing-collections" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.MARKETING_COLLECTIONS_MANAGE}>
                  <MarketingCollectionsPage />
                </ProtectedRoute>
              } />
              <Route path="products/create" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_CREATE}>
                  <CreateProductPage />
                </ProtectedRoute>
              } />
              <Route path="products/drafts" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCT_DRAFTS_VIEW}>
                  <DraftProductsPage />
                </ProtectedRoute>
              } />
              <Route path="products/drafts/:draftId" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCT_DRAFTS_VIEW}>
                  <DraftReviewPage />
                </ProtectedRoute>
              } />
              <Route path="products/drafts/:draftId/edit" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_CREATE}>
                  <CreateProductPage />
                </ProtectedRoute>
              } />
              <Route path="products/approval" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_APPROVE}>
                  <ProductApprovalPage />
                </ProtectedRoute>
              } />
              <Route path="products/view/:productId" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_VIEW}>
                  <ProductDetailPage />
                </ProtectedRoute>
              } />
              <Route path="products/edit/:productId" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
                  <EditProductPage />
                </ProtectedRoute>
              } />
              <Route path="product-types" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCT_TYPES_MANAGE}>
                  <ProductTypesManagementPage />
                </ProtectedRoute>
              } />
              <Route path="product-tags" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCT_TAGS_MANAGE}>
                  <ProductTagsManagementPage />
                </ProtectedRoute>
              } />
              <Route path="product-options" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCT_OPTIONS_MANAGE}>
                  <ProductOptionsManagementPage />
                </ProtectedRoute>
              } />
              <Route path="custom-fields" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CUSTOM_FIELDS_MANAGE}>
                  <CustomFieldsManagementPage />
                </ProtectedRoute>
              } />
              <Route path="ledger" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.LEDGER_VIEW}>
                  <LedgerPage />
                </ProtectedRoute>
              } />
              <Route path="invoices" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.INVOICES_VIEW}>
                  <InvoicesPage />
                </ProtectedRoute>
              } />
              <Route path="invoices/generate" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.INVOICES_GENERATE}>
                  <GenerateInvoicePage />
                </ProtectedRoute>
              } />
              <Route path="users" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USERS_VIEW}>
                  <UsersPage />
                </ProtectedRoute>
              } />
              <Route path="sellers-management" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SELLERS_VIEW}>
                  <SellersManagementPage />
                </ProtectedRoute>
              } />
              <Route path="shopify-sync/:sellerBrandId" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SELLER_BRANDS_SHOPIFY_CONNECT}>
                  <ShopifySyncSettingsPage />
                </ProtectedRoute>
              } />
              <Route path="historical-mappings" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.HISTORICAL_SYNC_VIEW}>
                  <HistoricalMappingsPage />
                </ProtectedRoute>
              } />
              <Route path="shopify-sync-config" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SHOPIFY_SYNC_CONFIG_MANAGE}>
                  <ShopifySyncConfig />
                </ProtectedRoute>
              } />
              <Route path="order-discounts" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.DISCOUNTS_VIEW}>
                  <OrderDiscountsPage />
                </ProtectedRoute>
              } />
              <Route path="pickup-list" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PICKUP_LIST_VIEW}>
                  <PickupListPage />
                </ProtectedRoute>
              } />
              <Route path="return-list" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.RETURN_LIST_VIEW}>
                  <ReturnListPage />
                </ProtectedRoute>
              } />
              <Route path="roles" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ROLES_VIEW}>
                  <RolesManagementPage />
                </ProtectedRoute>
              } />
              <Route path="shopify-sync-settings/:sellerBrandId" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SELLER_BRANDS_MANAGE}>
                  <ShopifySyncSettingsPage />
                </ProtectedRoute>
              } />
              <Route path="qr-code" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.QRCODE_GENERATE}>
                  <QRCodeGeneratorPage />
                </ProtectedRoute>
              } />
<Route path="change-password" element={<ChangePasswordPage />} />
              <Route path="notification-settings" element={<NotificationSettingsPage />} />
              <Route path="shipping-defaults" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_FULFILL}>
                  <ShippingDefaultsPage />
                </ProtectedRoute>
              } />
              <Route path="market-price-uplifts" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.MARKET_PRICE_UPLIFTS_MANAGE}>
                  <MarketPriceUpliftsPage />
                </ProtectedRoute>
              } />
              <Route path="whatsapp-button" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.WHATSAPP_BUTTON_MANAGE}>
                  <WhatsAppButtonPage />
                </ProtectedRoute>
              } />
              <Route path="search/analytics" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SEARCH_VIEW_ANALYTICS}>
                  <SearchAnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="search/mappings" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SEARCH_VIEW_MAPPINGS}>
                  <SearchMappingsPage />
                </ProtectedRoute>
              } />
              <Route path="search/filters" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SEARCH_MANAGE_FILTERS}>
                  <SearchFiltersPage />
                </ProtectedRoute>
              } />
              <Route path="search/image-vectors" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.IMAGE_VECTORS_MANAGE}>
                  <ImageVectorsPage />
                </ProtectedRoute>
              } />
              <Route path="search/catalog-rules" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CATALOGS_MANAGE_RULES}>
                  <CatalogRulesPage />
                </ProtectedRoute>
              } />
              <Route path="search/size-categories" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SIZE_CATEGORIES_MANAGE}>
                  <SizeCategoriesPage />
                </ProtectedRoute>
              } />
              <Route path="search/color-families" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COLOR_FAMILIES_MANAGE}>
                  <ColorFamiliesPage />
                </ProtectedRoute>
              } />
              <Route path="competitor-analysis" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COMPETITOR_ANALYSIS_VIEW}>
                  <CompetitorAnalysisPage />
                </ProtectedRoute>
              } />
              <Route path="returns/scan" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_RETURNS}>
                  <ReturnScanPage />
                </ProtectedRoute>
              } />
              <Route path="customers/blacklist" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CUSTOMERS_BLACKLIST}>
                  <CustomerBlacklistPage />
                </ProtectedRoute>
              } />
              <Route path="marketing/push" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.MARKETING_PUSH}>
                  <MarketingPushPage />
                </ProtectedRoute>
              } />
              <Route path="marketing/push/analytics" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.MARKETING_PUSH_ANALYTICS}>
                  <PushAnalyticsPage />
                </ProtectedRoute>
              } />
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </Suspense>
        </Router>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
